/* =========================================================
   Product page – <nx-pdp-head>
   Wishlist, share, smooth scroll to reviews, offer countdown
   (daily / fixed date / evergreen) and live viewers. The price
   card is re-rendered by assets/theme.js on variant change; a
   MutationObserver keeps the countdown in sync with sale state.
   ========================================================= */
(function () {
  'use strict';

  if (customElements.get('nx-pdp-head')) return;

  var WISHLIST_KEY = 'trendora:wishlist';

  function readWishlist() {
    try {
      var list = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage unavailable */
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function parseDate(value) {
    if (!value) return null;
    var clean = String(value).trim().replace(' ', 'T');
    var date = new Date(clean);
    return isNaN(date.getTime()) ? null : date;
  }

  class NxPdpHead extends HTMLElement {
    connectedCallback() {
      this.cleanup = [];
      this.setupWishlist();
      this.setupShare();
      this.setupReviewsLink();
      this.setupTimer();
      this.setupViewers();
    }

    disconnectedCallback() {
      this.cleanup.forEach(function (fn) { fn(); });
      this.cleanup = [];
      window.clearInterval(this.timerId);
      window.clearTimeout(this.viewerId);
      if (this.observer) this.observer.disconnect();
    }

    on(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      this.cleanup.push(function () { target.removeEventListener(type, handler, options); });
    }

    /* ---------------- Wishlist ---------------- */
    setupWishlist() {
      var button = this.querySelector('[data-wishlist]');
      var handle = this.dataset.handle;
      if (!button || !handle) return;

      function sync() {
        var saved = readWishlist().indexOf(handle) > -1;
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      }

      this.on(button, 'click', function () {
        var list = readWishlist();
        var index = list.indexOf(handle);
        if (index > -1) list.splice(index, 1);
        else list.push(handle);
        writeWishlist(list);
        sync();
        button.classList.remove('is-pop');
        void button.offsetWidth;
        button.classList.add('is-pop');
        document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handle: handle, list: list } }));
      });
      this.on(document, 'wishlist:change', sync);
      sync();
    }

    /* ---------------- Share ---------------- */
    setupShare() {
      var button = this.querySelector('[data-share]');
      if (!button) return;
      var toast = button.querySelector('[data-share-toast]');
      var copied = this.dataset.copied || 'Link copied';
      var hideId = null;

      function flash() {
        if (!toast) return;
        toast.textContent = copied;
        toast.classList.add('is-visible');
        window.clearTimeout(hideId);
        hideId = window.setTimeout(function () { toast.classList.remove('is-visible'); }, 1800);
      }

      this.on(button, 'click', function () {
        var url = button.dataset.url || window.location.href;
        if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
          navigator.share({ title: button.dataset.title || document.title, url: url }).catch(function () {});
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(flash).catch(flash);
        } else {
          var field = document.createElement('textarea');
          field.value = url;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          try { document.execCommand('copy'); } catch (error) { /* ignore */ }
          field.remove();
          flash();
        }
      });
    }

    /* ---------------- Reviews link ---------------- */
    setupReviewsLink() {
      var link = this.querySelector('[data-scroll-reviews]');
      if (!link) return;
      this.on(link, 'click', function (event) {
        var target = document.querySelector('nx-reviews, #reviews, [id*="__reviews"], .nxrv');
        if (!target) return;
        event.preventDefault();
        var header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nx-header-h')) || 80;
        var top = target.getBoundingClientRect().top + window.scrollY - header - 16;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    }

    /* ---------------- Countdown ---------------- */
    deadline() {
      var mode = this.dataset.timer;
      var now = new Date();
      if (mode === 'daily') {
        var end = new Date(now);
        end.setHours(23, 59, 59, 999);
        return end;
      }
      if (mode === 'fixed') {
        return parseDate(this.dataset.timerEnd);
      }
      if (mode === 'evergreen') {
        var hours = parseFloat(this.dataset.timerHours) || 12;
        var key = 'trendora:offer:' + (this.dataset.handle || 'store');
        var stored = null;
        try { stored = parseInt(window.localStorage.getItem(key), 10); } catch (error) { stored = null; }
        var span = hours * 3600000;
        if (!stored || stored < now.getTime()) {
          stored = now.getTime() + span;
          try { window.localStorage.setItem(key, String(stored)); } catch (error) { /* ignore */ }
        }
        return new Date(stored);
      }
      return null;
    }

    onSale() {
      var card = this.querySelector('[data-price-card]');
      return !!(card && card.dataset.onSale === 'true');
    }

    setupTimer() {
      var box = this.querySelector('[data-timer-box]');
      if (!box) return;
      var self = this;
      var parts = {
        d: box.querySelector('[data-t="d"]'),
        h: box.querySelector('[data-t="h"]'),
        m: box.querySelector('[data-t="m"]'),
        s: box.querySelector('[data-t="s"]')
      };
      var saleOnly = this.dataset.timerSaleOnly === 'true';
      var end = this.deadline();

      function render() {
        if (!end) {
          box.hidden = true;
          return;
        }
        var diff = end.getTime() - Date.now();
        if (diff <= 0) {
          if (self.dataset.timer === 'fixed') {
            box.hidden = true;
            window.clearInterval(self.timerId);
            return;
          }
          end = self.deadline();
          diff = end.getTime() - Date.now();
        }
        box.hidden = saleOnly && !self.onSale();
        var total = Math.floor(diff / 1000);
        var days = Math.floor(total / 86400);
        var hours = Math.floor((total % 86400) / 3600);
        var minutes = Math.floor((total % 3600) / 60);
        var seconds = total % 60;
        parts.d.textContent = pad(days);
        if (days === 0) parts.d.parentElement.setAttribute('data-empty', '');
        else parts.d.parentElement.removeAttribute('data-empty');
        parts.h.textContent = pad(hours);
        parts.m.textContent = pad(minutes);
        parts.s.textContent = pad(seconds);
      }

      render();
      this.timerId = window.setInterval(render, 1000);

      var wrap = this.querySelector('.nxph__price-wrap');
      if (wrap && 'MutationObserver' in window) {
        this.observer = new MutationObserver(render);
        this.observer.observe(wrap, { childList: true });
      }
    }

    /* ---------------- Live viewers ---------------- */
    setupViewers() {
      var row = this.querySelector('[data-viewers]');
      if (!row) return;
      var text = row.querySelector('[data-viewers-text]');
      var min = parseInt(this.dataset.viewersMin, 10) || 8;
      var max = Math.max(min + 1, parseInt(this.dataset.viewersMax, 10) || 30);
      var template = text.dataset.template || '[count] people are viewing this right now';
      var current = Math.floor(min + Math.random() * (max - min));
      var self = this;

      function paint(bump) {
        var parts = template.split('[count]');
        text.textContent = '';
        text.appendChild(document.createTextNode(parts[0] || ''));
        var number = document.createElement('b');
        number.textContent = current;
        if (bump) number.classList.add('is-bump');
        text.appendChild(number);
        text.appendChild(document.createTextNode(parts.slice(1).join('[count]')));
        if (bump) window.setTimeout(function () { number.classList.remove('is-bump'); }, 400);
      }

      function step() {
        var change = Math.floor(Math.random() * 5) - 2;
        current = Math.min(max, Math.max(min, current + change));
        paint(change !== 0);
        self.viewerId = window.setTimeout(step, 3500 + Math.random() * 4000);
      }

      paint(false);
      row.hidden = false;
      this.viewerId = window.setTimeout(step, 4000);
    }
  }

  customElements.define('nx-pdp-head', NxPdpHead);
})();
